import { useState, lazy, Suspense } from 'react';
import { Routes, Route, useLocation } from 'react-router-dom';
import { ROUTES, isWebdevDemoPath, isVbTippPath } from './config/routes.js';
import './App.css';
import Hero from './components/Hero.jsx';
import VideoSection from './components/VideoSection.jsx';
import SocialSection from './components/SocialSection.jsx';
import Footer from './components/Footer.jsx';
import HamburgerMenu from './components/HamburgerMenu.jsx';
import ErrorBoundary from './components/ErrorBoundary.jsx';
import ErrorFallback from './components/ErrorFallback.jsx';
import PrivacyPolicy from './components/PrivacyPolicy.jsx';
import CookieConsent from './components/CookieConsent.jsx';
import InterviewRoute from './pages/InterviewRoute.jsx';
// Eager, not code-split: the catch-all renders precisely when no routed page
// matched, so it must not itself depend on fetching a chunk that could fail.
import NotFoundPage from './pages/NotFoundPage.jsx';

// Code-split blog pages, vb-tipp, and webdev page to keep the main bundle lean
const BlogListingPage = lazy(() => import('./pages/BlogListingPage.jsx'));
const BlogArticlePage = lazy(() => import('./pages/BlogArticlePage.jsx'));
const VbTippRoute = lazy(() => import('./pages/vbtipp/VbTippRoute.jsx'));
const WebDevPage = lazy(() => import('./pages/WebDevPage.jsx'));
const ImpresszumPage = lazy(() => import('./pages/ImpresszumPage.jsx'));
// Demo/example client sites linked from the webdev pricing cards
const FormaFittDemoPage = lazy(() => import('./pages/webdev/FormaFittDemoPage.jsx'));
const DeruDentDemoPage = lazy(() => import('./pages/webdev/DeruDentDemoPage.jsx'));

function HomePage() {
  return (
    <div className="App">
      <Hero />
      <VideoSection />
      <SocialSection />
    </div>
  );
}

function App() {
  const { pathname } = useLocation();
  // The /weboldal-keszites/pelda/* demo pages mimic standalone client sites, so the
  // explainIT global nav is suppressed there; the demo banner's back-link is the way out.
  // Router-matched, not prefix-matched: a string prefix disagreed with the router on
  // casing and on paths that resolve to no route — see isWebdevDemoPath's docstring.
  const isWebDevDemo = isWebdevDemoPath(pathname);
  // Global marketing/legal chrome (footer + the privacy/cookie modals it opens).
  // Shown on the public content pages so the impresszum/privacy links and the cookie
  // banner are reachable site-wide (Ektv. 4.§ "folyamatosan, könnyen hozzáférhető").
  // Suppressed on the demo microsites and the vb-tipp app section, which carry their
  // own self-contained layouts.
  const showSiteChrome = !isWebDevDemo && !isVbTippPath(pathname);

  const [privacyOpen, setPrivacyOpen] = useState(false);
  const [cookieSettingsOpen, setCookieSettingsOpen] = useState(false);

  return (
    <>
      {/* Global nav, rendered on all pages outside Routes AND outside the
          ErrorBoundary — so it stays interactive even if a routed view crashes,
          letting the user navigate away from a broken page. Hidden on demo pages. */}
      {!isWebDevDemo && <HamburgerMenu />}

      {/* Single top-level boundary around the routed area: any route render error
          (or a lazy-chunk load failure from the code-split routes below) shows
          ErrorFallback instead of a blank screen. `resetKey={pathname}` makes the
          boundary auto-clear the error when the user navigates to a different
          route (so nav actually recovers the UI); ErrorFallback also offers an
          in-place retry and a full page reload for a deterministic same-route
          crash. */}
      <ErrorBoundary resetKey={pathname} fallback={ErrorFallback}>
        <Suspense fallback={null}>
          <Routes>
            <Route path="/" element={<HomePage />} />
            <Route path="/interview" element={<InterviewRoute />} />
            <Route path="/blogposts" element={<BlogListingPage />} />
            <Route path="/blogposts/:slug" element={<BlogArticlePage />} />
            <Route path="/vb-tipp/*" element={<VbTippRoute />} />
            <Route path={ROUTES.webdevService} element={<WebDevPage />} />
            <Route path={ROUTES.webdevDemoLanding} element={<FormaFittDemoPage />} />
            <Route path={ROUTES.webdevDemoBemutatkozo} element={<DeruDentDemoPage />} />
            <Route path={ROUTES.impresszum} element={<ImpresszumPage />} />
            {/* Catch-all. Without it an unmatched URL rendered an empty routed
                area — and under the demo prefix, where chrome is suppressed, a
                fully blank page with no way out. */}
            <Route path="*" element={<NotFoundPage />} />
          </Routes>
        </Suspense>
      </ErrorBoundary>

      {/* Global footer + the modals it triggers, rendered outside the routed
          ErrorBoundary so they survive a route crash. Lifted here from HomePage so
          the impresszum/privacy/cookie links and the cookie-consent banner are
          present on every public page, not just the homepage. */}
      {showSiteChrome && (
        <>
          <Footer
            onPrivacyClick={() => setPrivacyOpen(true)}
            onCookieSettingsClick={() => setCookieSettingsOpen(true)}
          />
          <PrivacyPolicy isOpen={privacyOpen} onClose={() => setPrivacyOpen(false)} />
          <CookieConsent
            onPrivacyClick={() => setPrivacyOpen(true)}
            forceOpen={cookieSettingsOpen}
            onSettingsClose={() => setCookieSettingsOpen(false)}
          />
        </>
      )}
    </>
  );
}

export default App;
